import { BRAND } from '@onuw/roles-data'
import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router'
import { openPrivacySettings, usePrivacySettings } from '../features/ads/ConsentGate'
import { cx } from './Card'
import styles from './Prose.module.css'

/** A page above this one, on the way up to the site's home. */
export interface Crumb {
  to: string
  label: string
}

/**
 * Site nav and footer around the public pages; both keep the text width so they sit in the same place on every
 * page. `wide` gives the content alone the full container width (roles). The links end with a slash, the address
 * a prerendered page is served at.
 */
export function SitePage({ wide, children }: { wide?: boolean | undefined; children: ReactNode }) {
  const privacySettings = usePrivacySettings()
  return (
    <div className={cx(styles.page, wide && styles.wide)}>
      <nav className={styles.nav} aria-label="Site">
        <Link to="/" className={styles.brand}>
          {BRAND.shortName}
        </Link>
        <Link to="/rules/">How to play</Link>
        <Link to="/roles/">Roles</Link>
        <Link to="/guides/">Guides</Link>
        <Link to="/faq/">FAQ</Link>
      </nav>
      <main className={styles.main}>{children}</main>
      <footer className={styles.footer}>
        <p>
          <Link to="/privacy/">Privacy</Link> · <Link to="/terms/">Terms</Link>
          {privacySettings && (
            <>
              {' '}
              ·{' '}
              <button type="button" className={styles.linkButton} onClick={openPrivacySettings}>
                Privacy settings
              </button>
            </>
          )}
        </p>
      </footer>
    </div>
  )
}

/** The way back up from a page: "Roles › Seer". */
export function Crumbs({ trail, here }: { trail: Crumb[]; here: string }) {
  return (
    <nav className={styles.crumbs} aria-label="Breadcrumb">
      {trail.map((c) => (
        <Fragment key={c.to}>
          <Link to={c.to}>{c.label}</Link>
          <span aria-hidden>›</span>
        </Fragment>
      ))}
      <span aria-current="page">{here}</span>
    </nav>
  )
}

/** Layout for the written pages (rules, FAQ, guides, privacy, terms): readable measure, site nav, footer links. */
export function Prose({
  title,
  lead,
  crumbs,
  children,
}: {
  title: string
  lead?: string
  crumbs?: Crumb[] | undefined
  children: ReactNode
}) {
  return (
    <SitePage>
      <header className={styles.header}>
        {crumbs && <Crumbs trail={crumbs} here={title} />}
        <h1>{title}</h1>
        {lead && <p className={styles.lead}>{lead}</p>}
      </header>
      <article className={styles.article}>{children}</article>
    </SitePage>
  )
}
